/** Stacked (max-lg) tiles: icon above a reserved two-line label. */
export const CHOICE_CARD_STACKED_MIN_HEIGHT_PX = 104;
export const CHOICE_CARD_STACKED_PAD_Y_PX = 12;
export const CHOICE_CARD_STACKED_ICON_PX = 38;
export const CHOICE_CARD_STACKED_GAP_PX = 12;
export const CHOICE_LABEL_LINE_HEIGHT = 1.3;
export const CHOICE_LABEL_RESERVED_LINES = 2;
export const CHOICE_LABEL_STACKED_MIN_PX = 14;
export const CHOICE_LABEL_STACKED_MAX_PX = 16;
/** 3.7vw — matches `clamp(14px, 3.7vw, 16px)` on stacked labels. */
export const CHOICE_LABEL_STACKED_VW = 3.7;

/**
 * Desktop tiles are landscape surfaces with a centred icon-over-label stack.
 * The label slot always reserves two lines so icons stay aligned when one
 * title wraps.
 */
export const CHOICE_CARD_DESKTOP_ICON_PX = 24;
export const CHOICE_CARD_DESKTOP_GAP_PX = 0;
/** Modest inset so the icon and label sit off the card edge. */
export const CHOICE_CARD_DESKTOP_PAD_Y_PX = 16;
/** Same type as the question bubble: 18px, medium, 1.3 line height. 16px below md. */
export const QUESTION_LABEL_TYPE_CLASS = "text-[18px] font-medium leading-[1.3] tracking-[-0.03em] max-md:text-[16px]";
/** Shared by journey choices and icon answer tiles. */
export const ICON_TEXT_TILE_SIZE_CLASS = QUESTION_LABEL_TYPE_CLASS;
export const CHOICE_CARD_DESKTOP_SIZE_CLASS = ICON_TEXT_TILE_SIZE_CLASS;

/**
 * One grid row of cards shares two tracks (icon, label) via subgrid, so every
 * icon sits on the same line and every card is as tall as the longest label.
 * The tracks size to their content — nothing is clipped.
 */
export const ICON_TEXT_TILE_CLASS = "row-span-2 grid h-full w-full grid-rows-subgrid";
export const ICON_TEXT_TOP_CLASS = "flex items-end justify-center px-4 pt-4 max-md:px-3 max-md:pt-3";
export const ICON_TEXT_BOTTOM_CLASS =
	"flex min-w-0 items-start justify-center px-4 pb-4 text-center max-md:px-3 max-md:pb-3";
export const ICON_TEXT_HALF_CLASS = ICON_TEXT_TOP_CLASS;
export const ICON_GLYPH_CLASS =
	"block h-6 w-auto object-contain max-md:h-5 group-hover/choice:brightness-0 group-hover/choice:invert group-hover/option:brightness-0 group-hover/option:invert";

/**
 * Desktop choice type still tracks viewport (16→26). Stacked tiles are only ~300px
 * wide, so the same vw formula inflates labels past the tile and they overflow.
 */
export const CHOICE_CARD_STACKED_SIZE_CLASS = "max-lg:h-auto";
/** Picker intro desktop label — fixed 18px so the smaller split tile still fits two lines. */
export const CHOICE_LABEL_PICKER_DESKTOP_PX = 18;
export const CHOICE_LABEL_PICKER_DESKTOP_SIZE_CLASS = "text-[18px] font-medium leading-[1.3] tracking-[-0.03em]";
export const CHOICE_LABEL_STACKED_SIZE_CLASS = "max-lg:text-[clamp(14px,3.7vw,16px)] max-lg:xshort:text-[13px]";
/** Two-line slot so a single-line title does not lift that card's icon. */
export const CHOICE_LABEL_RESERVE_CLASS = "min-h-[calc(2*1.3em)]";

export function stackedChoiceLabelFontSizePx(viewportWidthPx: number): number {
	const fluid = (viewportWidthPx * CHOICE_LABEL_STACKED_VW) / 100;
	return Math.min(CHOICE_LABEL_STACKED_MAX_PX, Math.max(CHOICE_LABEL_STACKED_MIN_PX, fluid));
}

export function stackedChoiceCardHeightPx({
	lines,
	fontSizePx,
}: {
	lines: number;
	fontSizePx: number;
}): number {
	const reservedLines = Math.max(lines, CHOICE_LABEL_RESERVED_LINES);
	const textHeight = reservedLines * fontSizePx * CHOICE_LABEL_LINE_HEIGHT;
	const content =
		CHOICE_CARD_STACKED_PAD_Y_PX * 2 + CHOICE_CARD_STACKED_ICON_PX + CHOICE_CARD_STACKED_GAP_PX + textHeight;
	return Math.max(CHOICE_CARD_STACKED_MIN_HEIGHT_PX, content);
}
